import { createContext, useContext } from 'react';
import type { VaultDocument, VaultSku, VaultStatus } from '@/lib/api/vault';
import type { RungLink } from './model';

/** Public, provider-optional part of the funnel state (Nav and the summon overlay read only this). */
export interface FunnelState {
  /** Rungs the visitor has earned, in ladder order. */
  readonly revealedRungs: readonly RungLink[];
}

export interface FunnelValue extends FunnelState {
  /** Last good /api/vault/status.php answer (merged with claims); null until one arrives. */
  readonly status: VaultStatus | null;
  readonly signature: string;
  readonly doc: (sku: VaultSku) => VaultDocument | null;
  /** Re-reads the status now; concurrent callers share one request. */
  readonly refresh: () => Promise<VaultStatus | null>;
}

export const FunnelContext = createContext<FunnelValue | null>(null);

const EMPTY_STATE: FunnelState = { revealedRungs: [] };

/** Ladder data for components that may render without the provider (Nav, summon overlay). */
export function useFunnelState(): FunnelState {
  return useContext(FunnelContext) ?? EMPTY_STATE;
}

/** Full funnel state for the funnel pages; requires FunnelProvider. */
export function useFunnel(): FunnelValue {
  const value = useContext(FunnelContext);
  if (value === null) throw new Error('useFunnel must be used within FunnelProvider');
  return value;
}
